/**
 * Everything drawn on top of the world.
 *
 * Kept to DOM rather than sprites, because the thing it has to survive is a
 * phone: a 4-inch screen, one hand holding it, and a player who has never seen
 * a shooter. So the health bar is enormous, the only text that ever moves is a
 * single line in the middle, and the one piece of navigation help — an arrow
 * to the exit — is always on screen so nobody is ever lost.
 */

import { ENDLESS, type RunState } from './run.ts';
import { CARDS, type Card } from './cards.ts';
import { INTRINSIC } from './traits.ts';
import { familyOf } from './weapons.ts';
import { rankOf, type ScoreRow } from './scores.ts';
import { LOOK_RANGE, type Settings } from './settings.ts';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
};


/** A colour mixed `t` of the way to white, for text that has to stay legible. */
function lighten(colour: number, t: number): string {
  const to = (v: number): number => Math.round(v + (255 - v) * t);
  const r = to((colour >> 16) & 0xff);
  const g = to((colour >> 8) & 0xff);
  const b = to(colour & 0xff);
  return `rgb(${r} ${g} ${b})`;
}

export class Hud {
  readonly root: HTMLElement;
  private health = el('div', 'hud-health-fill');
  /**
   * What the last few seconds cost you and you can still take back.
   *
   * Behind the health rather than beside it, so the bar reads as one length
   * that is partly solid and partly owed — the shape every game that has done
   * this uses, because it makes "how much of this can I get back" a glance
   * rather than a calculation.
   */
  private greyFill = el('div', 'hud-health-grey');
  private healthText = el('div', 'hud-health-text');
  private ammo = el('div', 'hud-ammo');
  private weapon = el('div', 'hud-weapon');
  /** The class swatch, alongside the name it is explaining. */
  private dot = el('span', 'hud-class-dot');
  private rule = el('div', 'hud-rule');
  private depth = el('div', 'hud-depth');
  /**
   * Three words saying what the floor wants.
   *
   * Under the floor number, in the corner nobody looks at during a fight and
   * everybody looks at on arrival — which is exactly when it needs to be read.
   * It never changes mid-floor, so it does not compete for attention with
   * anything that does.
   */
  private objective = el('div', 'hud-objective');
  private score = el('div', 'hud-score');
  private message = el('div', 'hud-message');
  private arrow = el('div', 'hud-arrow');
  private arrowLabel = el('div', 'hud-arrow-label');
  private crosshair = el('div', 'hud-crosshair');
  /** Top, bottom, left, right. Set once, moved every frame. */
  private ticks = [
    el('div', 'hud-tick v'), el('div', 'hud-tick v'),
    el('div', 'hud-tick h'), el('div', 'hud-tick h'),
  ];
  private arrowWrap = el('div', 'hud-arrow-wrap');
  private flash = el('div', 'hud-flash');
  private hurtArc = el('div', 'hud-hurt');
  /**
   * How close to dead, held rather than flashed.
   *
   * The damage wedge says which way it came from and is gone in half a second.
   * Nothing said "you are nearly out", which is the one piece of state a
   * player needs to change what they are doing rather than how they are
   * aiming — so it sits there and breathes until you find a medkit.
   */
  private critical = el('div', 'hud-critical');
  private cardScreen = el('div', 'screen cards');
  private deathScreen = el('div', 'screen death');
  private cardHost = el('div', 'card-row');
  private hint = el('div', 'hud-hint');
  private place = el('div', 'hud-place');
  private paused = el('div', 'hud-paused');
  private panel = el('div', 'settings');
  private gear = el('button', 'hud-gear', '\u2699');
  /** Called whenever the player moves a control. */
  onSettings: ((s: Settings) => void) | null = null;
  onResume: (() => void) | null = null;
  private remaining = el('div', 'hud-remaining');
  /** The signal's fill, on a hold floor. Hidden on every other. */
  private holdWrap = el('div', 'hud-hold');
  private holdFill = el('div', 'hud-hold-fill');

  constructor(parent: HTMLElement) {
    this.root = el('div', 'hud');

    const bar = el('div', 'hud-health');
    bar.append(this.greyFill, this.health, this.healthText);

    const left = el('div', 'hud-left');
    left.append(bar);

    const right = el('div', 'hud-right');
    right.append(this.ammo, this.weapon, this.rule);

    const top = el('div', 'hud-top');
    const leftTop = el('div', 'hud-top-left');
    leftTop.append(this.depth, this.objective, this.place);
    top.append(leftTop, this.score);

    this.arrowWrap.append(this.arrow, this.arrowLabel);
    const arrowWrap = this.arrowWrap;

    this.paused.style.display = 'none';
    this.paused.append(this.panel);
    this.gear.setAttribute('aria-label', 'Settings');
    this.holdWrap.append(this.holdFill);
    this.crosshair.append(...this.ticks);
    this.root.append(this.gear, this.paused, this.flash, this.critical, this.hurtArc, this.crosshair, arrowWrap, this.remaining, this.holdWrap, top, this.message, left, right, this.hint, this.cardScreen, this.deathScreen);
    parent.append(this.root);
    this.cardScreen.style.display = 'none';
    this.deathScreen.style.display = 'none';
  }

  setHint(text: string): void {
    this.hint.textContent = text;
  }

  /** The map's own name, so a floor is a place rather than a number. */
  setPlace(name: string, theme: string): void {
    this.place.textContent = name;
    this.place.title = theme;
  }

  setPaused(on: boolean): void {
    this.paused.style.display = on ? 'grid' : 'none';
    this.gear.style.display = on ? 'none' : 'block';
  }

  /** The gear in the corner, which is the only way in on a touchscreen. */
  onGear(fn: () => void): void {
    this.gear.addEventListener('click', (e) => {
      e.stopPropagation();
      fn();
    });
  }

  /**
   * Build the pause screen from the settings themselves.
   *
   * Every control writes straight through and calls back, so there is no
   * apply button and nothing to forget to press — which matters most on the
   * one control a player actually has to feel out by trial, the look speed.
   */
  buildSettings(settings: Settings): void {
    this.panel.replaceChildren();
    const write = (): void => this.onSettings?.(settings);

    this.panel.append(el('div', 'screen-kicker', 'Paused'), el('h1', undefined, 'Settings'));

    const row = (label: string, control: HTMLElement, note?: string): HTMLElement => {
      const r = el('div', 'settings-row');
      const left = el('div', 'settings-label', label);
      if (note) left.append(el('div', 'settings-note', note));
      r.append(left, control);
      this.panel.append(r);
      return r;
    };

    const toggle = (on: boolean, onChange: (v: boolean) => void): HTMLElement => {
      const b = el('button', `settings-toggle${on ? ' on' : ''}`, on ? 'On' : 'Off');
      b.addEventListener('click', () => {
        on = !on;
        b.textContent = on ? 'On' : 'Off';
        b.classList.toggle('on', on);
        onChange(on);
        write();
      });
      return b;
    };

    const slider = (
      value: number,
      range: readonly [number, number],
      onChange: (v: number) => void,
    ): HTMLElement => {
      const wrap = el('div', 'settings-slider');
      const input = el('input');
      input.type = 'range';
      input.min = '0';
      input.max = '100';
      input.step = '1';
      input.value = String(Math.round(((value - range[0]) / (range[1] - range[0])) * 100));
      const read = el('div', 'settings-value', `${input.value}`);
      input.addEventListener('input', () => {
        const t = Number(input.value) / 100;
        read.textContent = input.value;
        onChange(range[0] + t * (range[1] - range[0]));
        write();
      });
      wrap.append(input, read);
      return wrap;
    };

    const choice = <T extends string>(
      options: readonly T[],
      value: T,
      onChange: (v: T) => void,
    ): HTMLElement => {
      const wrap = el('div', 'settings-choice');
      for (const o of options) {
        const b = el('button', o === value ? 'on' : undefined, o);
        b.addEventListener('click', () => {
          for (const other of wrap.children) other.classList.remove('on');
          b.classList.add('on');
          onChange(o);
          write();
        });
        wrap.append(b);
      }
      return wrap;
    };

    row('Sound', toggle(settings.sound, (v) => { settings.sound = v; }));
    row('Look speed — touch', slider(settings.touchLook, LOOK_RANGE.touchLook, (v) => { settings.touchLook = v; }));
    row('Look speed — mouse', slider(settings.mouseLook, LOOK_RANGE.mouseLook, (v) => { settings.mouseLook = v; }));
    row('Invert vertical', toggle(settings.invertY, (v) => { settings.invertY = v; }));
    row(
      'Quality',
      choice(['auto', 'mobile', 'balanced', 'high'] as const, settings.quality, (v) => { settings.quality = v; }),
      'Resolution changes now; how much town is built changes on the next floor.',
    );

    const resume = el('button', 'settings-resume', 'Resume');
    resume.addEventListener('click', () => this.onResume?.());
    this.panel.append(resume);
    // The reminder is only a reminder if it names controls this player has.
    this.panel.append(
      el(
        'div',
        'settings-note',
        this.root.classList.contains('touch')
          ? 'Left thumb moves · drag right to look · hold the circle to fire'
          : 'Esc or M anywhere · WASD move · R reload · Q swap',
      ),
    );
  }

  setTouch(on: boolean): void {
    this.root.classList.toggle('touch', on);
  }

  update(s: RunState): void {
    const pct = Math.max(0, s.health / s.maxHealth);
    this.health.style.width = `${pct * 100}%`;
    this.health.style.background = pct > 0.55 ? '#4fd67f' : pct > 0.25 ? '#f0c247' : '#e8544a';
    this.healthText.textContent = `${Math.ceil(s.health)}`;
    const owed = Math.max(0, Math.min(s.maxHealth - s.health, s.grey));
    this.greyFill.style.width = `${((s.health + owed) / s.maxHealth) * 100}%`;
    // The endless sidearm shows no reserve at all rather than a number that
    // never moves.
    this.ammo.textContent = s.reloading
      ? 'RELOADING'
      : `${s.ammo}${s.reserve >= ENDLESS ? '' : ` / ${s.reserve}`}`;
    this.ammo.classList.toggle('low', !s.reloading && s.ammo <= 3);
    this.weapon.textContent = s.weapon.name;
    /*
     * And what family it belongs to, in that family's colour.
     *
     * The name alone does not tell you what you are holding — nobody arrives
     * knowing whether a "Vz. 61" is a sidearm or a carbine — and the class was
     * shown nowhere at all. The colour matches the stripe on the receiver, so
     * the two teach each other: read it once on the HUD, and after that the
     * stripe on a gun lying on the floor is enough.
     */
    const fam = familyOf(s.weapon.cls);
    const hex = `#${fam.colour.toString(16).padStart(6, '0')}`;
    /*
     * The swatch takes the colour at full strength; the name only leans that
     * way.
     *
     * A saturated hue is what makes a 12px square identifiable, and it is also
     * what makes 11px letterspaced text unreadable against a white wall —
     * measured by looking at the marksman violet and the sidearm blue over a
     * sunlit exterior. Mixing the name most of the way to white keeps the
     * association and gives the glyphs back their contrast.
     */
    this.weapon.style.color = lighten(fam.colour, 0.55);
    this.dot.style.background = hex;
    // `textContent` above cleared the children, so the swatch goes back in.
    this.weapon.prepend(this.dot);
    // The rule the action plays by, under its name. Permanent and small: it
    // is a thing to glance at once and then know, not a thing to read.
    this.rule.textContent = `${fam.name} · ${INTRINSIC[s.weapon.action].name}`;
    this.depth.textContent = `FLOOR ${s.depth}`;
    this.objective.textContent = s.exitOpen ? 'FIND THE WAY OUT' : s.objective;
    this.score.textContent = `${s.score}`;

    this.message.textContent = s.messageFor > 0 ? s.message : '';
    this.message.style.opacity = `${Math.min(1, s.messageFor * 2)}`;

    // Before the floor is clear there is nowhere to point, so the same slot
    // carries the only thing that matters: how many are left.
    const pointing = s.exitOpen || s.huntMode;
    this.arrowWrap.style.display = pointing ? 'grid' : 'none';
    this.remaining.style.display = s.exitOpen ? 'none' : 'block';
    if (pointing) {
      const deg = (s.exitBearing * 180) / Math.PI;
      this.arrow.style.transform = `rotate(${-deg}deg)`;
      this.arrowLabel.textContent = `${Math.round(s.exitDistance)}m`;
      this.arrow.classList.toggle('close', s.exitDistance < 14);
      // Hunting the stragglers and heading for the door are different jobs,
      // so the arrow changes colour rather than silently meaning two things.
      this.arrowWrap.classList.toggle('hunt', !s.exitOpen);
    }
    if (!s.exitOpen) {
      /*
       * What is left, and where you are in the floor.
       *
       * A count on its own answers "how much more of this" and nothing else. A
       * count plus a position answers "how far through am I", which is the
       * question a player actually has while they are fighting — and it is the
       * only place the zones appear as a number rather than as ground.
       */
      const left = Math.max(s.enemiesAlive, s.remaining);
      const where = s.zones > 1 && s.contract === 'sweep' ? ` · ZONE ${s.zone}/${s.zones}` : '';
      this.remaining.textContent = left > 0 ? `${left} LEFT${where}` : where.replace(' · ', '');
    }

    // The signal, filling. Only ever on screen on the floors that have one.
    const holding = !s.exitOpen && s.hold >= 0;
    this.holdWrap.style.display = holding ? 'block' : 'none';
    if (holding) {
      this.holdFill.style.width = `${Math.round(s.hold * 100)}%`;
      this.holdWrap.classList.toggle('full', s.hold > 0.995);
    }

    // Comes in over the last third and deepens as it goes — and goes entirely
    // once you are dead, because the death screen has its own thing to say.
    const dire = s.phase === 'playing' ? Math.max(0, 1 - pct / 0.36) : 0;
    this.critical.style.opacity = `${dire * 0.85}`;
    this.critical.classList.toggle('beating', dire > 0.05);

    this.crosshair.classList.toggle('hit', s.hitFlash > 0.25);
    /*
     * Put the ticks at the edge of the cone, in pixels.
     *
     * The cone is an angle and the screen is a plane, so the conversion is the
     * projection the camera already does: half the viewport divided by the
     * tangent of half the vertical field, times the tangent of the spread. A
     * floor of two pixels so the ticks stay visible either side of the dot, and
     * a ceiling because a shotgun's cone would otherwise leave the screen.
     *
     * The floor was five, which on a phone held sideways is most of the range:
     * at 375 pixels tall a rifle's cone is three pixels and a sniper's is a
     * fifth of one, so both clamped to five and every accurate weapon in the
     * game had the same sight. Two keeps them apart where there is room and
     * still puts something either side of the dot where there is not — and a
     * reticle that collapses almost onto its own centre is the honest picture
     * of a gun that shoots where you point it.
     */
    const half = innerHeight / 2 / Math.tan((78 * Math.PI) / 180 / 2);
    const gap = Math.max(2, Math.min(innerHeight * 0.3, Math.tan(s.spread) * half));
    this.ticks[0].style.transform = `translate(-50%, ${-gap - 9}px)`;
    this.ticks[1].style.transform = `translate(-50%, ${gap}px)`;
    this.ticks[2].style.transform = `translate(${-gap - 9}px, -50%)`;
    this.ticks[3].style.transform = `translate(${gap}px, -50%)`;
    const dmg = s.damageFlash;
    // A wedge pointing at whatever hit you, so "turn around" is legible
    // without any text and without having to find the enemy first.
    this.hurtArc.style.opacity = `${dmg}`;
    this.hurtArc.style.transform = `rotate(${(-s.damageBearing * 180) / Math.PI}deg)`;
    const pick = s.pickupFlash;
    this.flash.style.background = dmg > 0
      ? `radial-gradient(ellipse at center, rgba(0,0,0,0) 35%, rgba(190,30,25,${0.6 * dmg}) 100%)`
      : pick > 0
        ? `radial-gradient(ellipse at center, rgba(0,0,0,0) 45%, rgba(90,220,140,${0.35 * pick}) 100%)`
        : 'none';
  }

  showCards(cards: Card[], depth: number, onPick: (c: Card) => void, taken: string[] = []): void {
    this.cardHost.replaceChildren();
    this.cardScreen.replaceChildren();
    this.cardScreen.append(
      el('div', 'screen-kicker', `Floor ${depth} cleared`),
      el('h1', undefined, 'Take one'),
      this.cardHost,
    );
    /*
     * What you already have, as a set of ideas rather than a list of cards.
     *
     * A card that builds on something taken three floors ago is the whole
     * point of a run becoming a build, and it is invisible unless the screen
     * says so. Marking it beats making the player remember.
     */
    const held = new Set<string>();
    for (const id of taken) for (const t of CARDS.find((x) => x.id === id)?.tags ?? []) held.add(t);

    for (const c of cards) {
      const b = el('button', `card tier-${c.tier}`);
      b.append(el('div', 'card-name', c.name), el('div', 'card-text', c.text));
      const tags = el('div', 'card-tags');
      for (const t of c.tags) {
        const tag = el('span', held.has(t) ? 'card-tag on' : 'card-tag', t);
        tags.append(tag);
      }
      b.append(tags);
      b.addEventListener('click', () => {
        this.cardScreen.style.display = 'none';
        onPick(c);
      });
      this.cardHost.append(b);
    }
    this.cardScreen.style.display = 'flex';
  }

  showDeath(s: RunState, rows: ScoreRow[], mine: ScoreRow, onRestart: () => void): void {
    this.deathScreen.replaceChildren();
    const rank = rankOf(rows, mine);
    const best = rows[0];
    const beat = best && best.at !== mine.at ? best.score - mine.score : 0;

    const head = el('div', 'death-head');
    head.append(
      /*
       * What killed you, in the kicker, because it is the first thing a player
       * wants to know and the last thing this screen used to say. "You died on
       * floor nine" attributes nothing; a death you cannot attribute is a
       * death you cannot learn anything from, and learning something is the
       * only reason to press Again.
       */
      el('div', 'screen-kicker', s.killedBy
        ? `Floor ${s.depth} — ${s.killedBy}`
        : `You died on floor ${s.depth}`),
      el('h1', undefined, `${s.score}`),
      el('div', 'death-sub', `${s.kills} kills · ${Math.floor(s.elapsed / 60)}m ${Math.round(s.elapsed % 60)}s`),
    );

    /*
     * And what you were, which is the other half of the same question.
     *
     * A run's identity is the gun in your hands and the four or five rules you
     * picked up on the way, and by floor nine nobody remembers what the third
     * one was. Naming them at the end is what turns a run into something you
     * can describe to somebody else — which is the whole of a roguelite's
     * social life.
     */
    const build = el('div', 'death-build');
    build.append(el('span', 'build-gun', s.weapon.name));
    for (const id of s.cards) {
      const card = CARDS.find((c) => c.id === id);
      if (card) build.append(el('span', 'build-card', card.name));
    }
    if (!s.cards.length) build.append(el('span', 'build-card', 'no upgrades'));
    head.append(build);

    const board = el('div', 'board');
    board.append(el('div', 'board-title', rank === 1 ? 'A new best' : 'Your best runs'));
    const table = el('div', 'board-rows');
    if (rows.length === 0) table.append(el('div', 'board-empty', 'This was your first run.'));
    for (const [i, r] of rows.slice(0, 8).entries()) {
      const row = el('div', `board-row${r.at === mine.at ? ' me' : ''}`);
      row.append(
        el('span', 'board-rank', `${i + 1}`),
        el('span', 'board-score', `${r.score}`),
        el('span', 'board-depth', `floor ${r.depth}`),
        el('span', 'board-kills', `${r.kills} kills`),
      );
      table.append(row);
    }
    board.append(table);
    if (beat > 0) board.append(el('div', 'board-gap', `${beat} points off your best.`));

    const again = el('button', 'big', 'Again');
    again.addEventListener('click', () => {
      this.deathScreen.style.display = 'none';
      onRestart();
    });

    /*
     * The seed, and a way to take it with you.
     *
     * A run is now entirely a function of this word — the floors, the order
     * they come in, and the cards you are offered on each — so it is worth
     * something to whoever wants that run again: to show somebody, or to hand
     * back with a bug.
     */
    const seedRow = el('div', 'death-seed');
    const label = el('code', undefined, mine.seed);
    const copy = el('button', 'seed-copy', 'Copy link');
    copy.addEventListener('click', () => {
      const url = `${location.origin}${location.pathname}?seed=${encodeURIComponent(mine.seed)}`;
      void navigator.clipboard?.writeText(url).then(
        () => { copy.textContent = 'Copied'; },
        () => { copy.textContent = url; },
      );
    });
    /*
     * And a way to hand somebody today's run instead of yours.
     *
     * The only social affordance a single-player game gets for free: two
     * people who have never spoken can compare a score if they were handed the
     * same floors, and a date is a handle everybody already has.
     */
    const today = el('button', 'seed-copy', 'Play today');
    today.addEventListener('click', () => {
      location.href = `${location.origin}${location.pathname}?daily`;
    });
    seedRow.append(el('span', undefined, 'Seed'), label, copy, today);

    this.deathScreen.append(head, board, seedRow, again);
    this.deathScreen.style.display = 'flex';
  }

  hideScreens(): void {
    this.cardScreen.style.display = 'none';
    this.deathScreen.style.display = 'none';
  }

  get screenOpen(): boolean {
    return this.cardScreen.style.display !== 'none' || this.deathScreen.style.display !== 'none';
  }
}
